import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { ProjectStorageAdapter } from '@lightscape/storage-adapter'
import { LocalProjectStorageAdapter } from './LocalAdapter'
import { ServerProjectStorageAdapter } from './RestAdapter'

export type StorageBackend = {
  adapter: ProjectStorageAdapter
  kind: 'server' | 'local'
  apiUrl?: string
}

const StorageContext = createContext<StorageBackend | null>(null)

const createBackend = (): StorageBackend => {
  const target = import.meta.env.VITE_STORAGE
  const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3000'
  if (target === 'server') {
    console.info(`[studio] storage adapter: server (${apiUrl})`)
    return { adapter: new ServerProjectStorageAdapter(apiUrl), kind: 'server', apiUrl }
  }
  console.info('[studio] storage adapter: local (IndexedDB)')
  return { adapter: new LocalProjectStorageAdapter(), kind: 'local' }
}

export function StorageProvider({ children }: { children: ReactNode }) {
  const backend = useMemo(() => createBackend(), [])
  return <StorageContext.Provider value={backend}>{children}</StorageContext.Provider>
}

export const useStorage = (): ProjectStorageAdapter => {
  const backend = useContext(StorageContext)
  if (!backend) throw new Error('StorageProvider missing')
  return backend.adapter
}

export const useStorageBackend = (): StorageBackend => {
  const backend = useContext(StorageContext)
  if (!backend) throw new Error('StorageProvider missing')
  return backend
}
